import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Alert, Segmented } from 'antd';
import { login } from '../services/api';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

export default function LoginPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginType, setLoginType] = useState('email');
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionExpired, setSessionExpired] = useState(searchParams.get('expired') === 'true');

  const handleTabChange = (val) => {
    setLoginType(val);
    form.resetFields();
    setError('');
    setSessionExpired(false);
  };

  const onFinish = async (values) => {
    setLoading(true); setError('');
    try {
      const payload = loginType === 'email'
        ? { email: values.email, password: values.password }
        : { username: values.username, password: values.password };

      const { data } = await login(payload);
      loginUser(data.access, data.refresh, data.user);
      navigate('/app-selection');

    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: '100vh',
      background: '#f8f9fa'
    }}>
      <style>{`
        /* Override Chrome Autofill background color */
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active,
        .ant-input:-webkit-autofill,
        .ant-input:-webkit-autofill:hover,
        .ant-input:-webkit-autofill:focus,
        .ant-input:-webkit-autofill:active,
        .ant-input-affix-wrapper input:-webkit-autofill,
        .ant-input-affix-wrapper input:-webkit-autofill:hover,
        .ant-input-affix-wrapper input:-webkit-autofill:focus,
        .ant-input-affix-wrapper input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
            -webkit-text-fill-color: #333333 !important;
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            background-color: #ffffff !important;
        }

        /* Ensure Ant Design Input wrappers and inputs have white background */
        .ant-input {
            background-color: #ffffff !important;
        }
        .ant-input-affix-wrapper {
            background-color: #ffffff !important;
        }
        .ant-input-affix-wrapper .ant-input {
            background-color: #ffffff !important;
        }
      `}</style>

      {/* Header */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        height: '64px',
        padding: '0 24px',
        background: '#ffffff',
        borderBottom: '1px solid #f0f0f0',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <img src={logo} alt="Apraava Energy" style={{ height: '32px', objectFit: 'contain' }} />
      </header>

      {/* Main Content */}
      <main style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        padding: '24px'
      }}>
        <Card
          style={{
            width: 440,
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f0f0f0',
            overflow: 'hidden'
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Top Multi-color Stripe */}
          <div style={{
            height: 4,
            background: 'linear-gradient(to right, #1b75bc 0%, #1b75bc 25%, #8cc63f 25%, #8cc63f 50%, #f7931e 50%, #f7931e 75%, #f05a28 75%, #f05a28 100%)'
          }} />

          {/* Form Content */}
          <div style={{ padding: '36px 32px' }}>
            <Typography.Title level={3} style={{ textAlign: 'center', marginBottom: 4, fontWeight: 600, color: '#1f1f1f' }}>
              Sign In
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 24, fontSize: 13, color: '#8c8c8c' }}>
              Sign in to access Apraava Energy modules
            </Typography.Paragraph>

            {error ? (
              <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />
            ) : sessionExpired ? (
              <Alert message="Session Expired" type="warning" showIcon style={{ marginBottom: 16 }} />
            ) : null}

            <Form
              form={form}
              onFinish={onFinish}
              onValuesChange={() => {
                setError('');
                setSessionExpired(false);
              }}
              size="large"
              layout="vertical"
              requiredMark={false}
            >
              <Segmented
                block
                size="large"
                options={[
                  { label: 'Email', value: 'email' },
                  { label: 'Username', value: 'username' }
                ]}
                value={loginType}
                onChange={handleTabChange}
                style={{ marginBottom: 18, padding: 4 }}
              />

              {loginType === 'email' ? (
                <Form.Item
                  key="email"
                  name="email"
                  label={<span style={{ fontWeight: 600, color: '#434343', fontSize: 14 }}>Email</span>}
                  labelCol={{ style: { paddingBottom: '0px' } }}
                  style={{ marginBottom: '16px' }}
                  rules={[{ required: true, message: 'Enter email' }, { type: 'email', message: 'Enter a valid email' }]}
                >
                  <Input placeholder="Enter your email" style={{ borderRadius: 6, backgroundColor: '#ffffff' }} />
                </Form.Item>
              ) : (
                <Form.Item
                  key="username"
                  name="username"
                  label={<span style={{ fontWeight: 600, color: '#434343', fontSize: 14 }}>Username</span>}
                  labelCol={{ style: { paddingBottom: '0px' } }}
                  style={{ marginBottom: '16px' }}
                  rules={[{ required: true, message: 'Enter username' }]}
                >
                  <Input placeholder="Enter your username" style={{ borderRadius: 6, backgroundColor: '#ffffff' }} />
                </Form.Item>
              )}

              <Form.Item
                name="password"
                label={<span style={{ fontWeight: 600, color: '#434343', fontSize: 14 }}>Password</span>}
                labelCol={{ style: { paddingBottom: '0px' } }}
                style={{ marginBottom: '20px' }}
                rules={[{ required: true, message: 'Enter password' }]}
              >
                <Input.Password placeholder="Enter your password" style={{ borderRadius: 6, backgroundColor: '#ffffff' }} />
              </Form.Item>

              <Button
                type="text"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  backgroundColor: '#d6e4ff',
                  color: '#002c8c',
                  fontWeight: 600,
                  height: '44px',
                  border: 'none',
                  borderRadius: '8px',
                  marginTop: 8
                }}
              >
                Sign In
              </Button>
            </Form>
          </div>
        </Card>
      </main>

      {/* Footer */}
      <footer style={{
        padding: '16px',
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <Typography.Text type="secondary" style={{ fontSize: 12, color: '#8c8c8c' }}>
          © 2026 Apraava Energy Private Limited. All rights reserved.
        </Typography.Text>
      </footer>
    </div>
  );
}

